import { Store } from "vuex";
import { Controllers, Portal } from "@infoland/iprova-api";
import { namespace as portalNamespace, PortalCollectionState, PortalInfoDictionary } from "../modules/portal/portal";
import { PortalSettings } from "@/types/portal";
import { Collection } from "@/types/collection/collection";
import { Constants } from "@/constants";

interface PortalCollection {
    collection_id: number;
    name: string;
    include_sub_collections: boolean;
    expanded: boolean;
    selected: boolean;
    order: number;
    parent_collection_id?: number;
    ancestor_collection_ids: number[];
}

export class PortalStore {
    private store: Store<any>;

    constructor(store: Store<any>) {
        this.store = store;

        /** Loads the default portal info */
        if (this.store) this.store.dispatch(`${portalNamespace}/loadDefaultPortalSettings`);
    }

    /** Gets the portals for the logged user */
    public get portals(): Portal[] {
        return this.store.getters[`${portalNamespace}/portals`];
    }

    public get defaultPortal(): PortalSettings {
        return this.store.getters[`${portalNamespace}/defaultPortal`];
    }

    /***
     * get a specific portal
     */
    public getPortal(portalId: number): Portal {
        return this.portals?.find(p => p.portal_id == portalId);
    }

    /***
     * Gets the collections to browse a portal
     */
    public async getCollections(portalId: number): Promise<Collection[]> {
        const data: PortalInfoDictionary<Collection[]> = this.store.getters[`${portalNamespace}/collections`];
        if (data[portalId] != undefined) {
            return data[portalId];
        } else {
            const portalDetails: Portal = await Controllers.PortalController.getPortal(portalId, true, true);
            if (!portalDetails) {
                return [];
            }

            // retrieve a flat list of all portal collections including states
            const portalCollections: PortalCollection[] = await Controllers.GenericController.getAsync<PortalCollection[]>(
                `/portals/${portalId}/collections/?include_sub_tree=true`
            );

            //parse the details info to a portalCOllection State
            const collectionsState: PortalCollectionState = {
                selectedCollectionId: portalDetails.selected_collection?.collection_id,
                expandedCollectionIds: portalDetails.expanded_collections?.map(c => c.collection_id)
            };

            //tree of collection for the portal
            const collectionsTree: Collection[] = [];

            //create portal collections lookup
            let collectionLookup: { [key: number]: Collection } = {};
            for (let portalCollection of portalCollections) {
                collectionLookup[portalCollection.collection_id] = {
                    collection_id: portalCollection.collection_id,
                    name: portalCollection.name,
                    order: portalCollection.order,
                    parent_collection_id: portalCollection.parent_collection_id,
                    children: [],
                    expanded: false,
                    selected: false
                };
            }

            let orphans: { [key: number]: Collection } = {};
            for (let portalCollection of portalCollections) {
                const collection = collectionLookup[portalCollection.collection_id];

                //does this collection have a parent?
                if (!collection.parent_collection_id) {
                    //add this item to the root of collections
                    collectionsTree.push(collection);
                } else {
                    let parent_id = this.getFirstPortalCollectionAncestorId(portalCollection, portalCollections) ?? collection.parent_collection_id;
                    //find parent in the flat list, and add it to the children of that item
                    const parent = parent_id ? collectionLookup[parent_id] : null;
                    if (parent) {
                        //add to parent
                        parent.children.push(collection);
                        //this.sortRecursive(parent.children);
                    } else if (orphans[parent_id]) {
                        //parent is orphan, add to that orphan
                        orphans[parent_id].children.push(collection);
                    } else {
                        //add to orphans
                        orphans[collection.collection_id] = collection;
                    }
                }
            }
            //any orphans remaining? add those to root collections
            collectionsTree.push(...Object.keys(orphans).map(id => orphans[id]));

            // Sort the collections based on the order property
            this.sortRecursive(collectionsTree);

            //put data in store (for next time)
            this.store.commit(`${portalNamespace}/portalCollections`, { portalId: portalId, collections: collectionsTree });
            this.store.commit(`${portalNamespace}/portalCollectionState`, { portalId: portalId, collectionState: collectionsState });

            //return data
            return collectionsTree;
        }
    }

    /** find first relative parent in the portal collections */
    private getFirstPortalCollectionAncestorId(portalCollection: PortalCollection, portalCollections: PortalCollection[]): number | null {
        for (let i = portalCollection.ancestor_collection_ids.length - 1; i >= 0; i--) {
            let ancestorId = portalCollection.ancestor_collection_ids[i];
            for (let j = 0; j < portalCollections.length; j++) {
                if (portalCollections[j].collection_id === ancestorId) return portalCollections[j].collection_id;
            }
        }
        return null;
    }

    /** Loads the portals for the logged user */
    public loadPortals(): Promise<Portal[]> {
        return this.store.dispatch(`${portalNamespace}/getPortals`);
    }

    /** Sorts collection children recursively*/
    public sortRecursive(collectionTreeChildren: Collection[]) {
        for (let i = 0; i < collectionTreeChildren.length; i++) {
            if (collectionTreeChildren[i].children && collectionTreeChildren[i].children.length > 0) {
                this.sortRecursive(collectionTreeChildren[i].children);
            }
        }
        collectionTreeChildren.sort((a, b) => (a.order > b.order ? 1 : -1));
    }

    /** Save the portal */
    public async savePortal(id: number, portalProps: Portal): Promise<boolean | null> {
        return this.store.dispatch(`${portalNamespace}/savePortal`, { id, portalProps });
    }

    /**
     * Save the selected collection for the given portal
     * @param portalId ID of the portal
     * @param collectionId ID of the selected collection
     */
    public setSelectedCollection(portalId: number, collectionId: number) {
        this.store.commit(`${portalNamespace}/selectedCollection`, { portalId: portalId, collectionId: collectionId });
    }

    /**
     * Save which collections are expanded for the given portal
     * @param portalId ID of the portal
     * @param collectionIds The collection IDs to save as expanded
     */
    public setExpandedCollections(portalId: number, collectionIds: number[]) {
        this.store.commit(`${portalNamespace}/expandedCollections`, { portalId: portalId, collectionIds: collectionIds });
    }

    /**
     * Get the ID of the selcted collection in a portal
     * @param portalId ID of the portal
     */
    public getSelectedCollectionID(portalId: number): number {
        //use default from portal (should be ready)
        const stateData: PortalInfoDictionary<PortalCollectionState> = this.store.getters[`${portalNamespace}/collectionStates`];
        if (stateData?.[portalId]) {
            return stateData[portalId].selectedCollectionId;
        } else {
            return Constants.Collection.AllCollectionsId;
        }
    }

    /**
     * Gets the IDs of the collections that are expanded in a portal
     * @param portalId ID of the portal
     */
    public getExpandedCollectionIDs(portalId: number): number[] {
        // Get states from personal store, before rather than the portal defined state
        const stateData: PortalInfoDictionary<PortalCollectionState> = this.store.getters[`${portalNamespace}/collectionStates`];

        if (Array.isArray(stateData?.[portalId]?.expandedCollectionIds)) {
            return stateData[portalId].expandedCollectionIds;
        } else {
            return [];
        }
    }
}
